import { AppRouteConfig, breadcrumbs } from '..';
import { jobManageApplicationsRoutes } from './job-application';

export enum JobRouteName {
	jobs = 'Jobs',
	draftJobList = 'DraftJobList',
	currentJobList = 'CurrentJobList',
	allJobList = 'AllJobList',
	newJob = 'NewJob',
	editJob = 'EditJob',
	editJobMain = 'EditJobMain',
	serviceHourList = 'ServiceHourList',
	serviceHourSessionList = 'ServiceHourSessionList',
	internalJobAttendanceImportList = 'InternalJobAttendanceImportList',
	internalJobContactDetails = 'InternalJobContactDetails',
	internalJobContactSearchList = 'InternalJobContactSearchList',
	internalJobContactSelectList = 'InternalJobContactSelectList',
	jobAttendanceList = 'JobAttendanceList',
	jobAttendanceListScanProfile = 'JobAttendanceListScanProfile',
	jobAttendanceNewApplication = 'JobAttendanceNewApplication',
	jobAttendanceNewApplicationContactSearchList = 'JobAttendanceNewApplicationContactSearchList',
	jobAttendanceImportList = 'JobAttendanceImportList',
	jobContactDetails = 'JobContactDetails',
	jobContactSearchList = 'JobContactSearchList',
	jobContactSelectList = 'JobContactSelectList',
	jobApplicationList = 'JobApplicationList',
	jobApplicationListContactSearchList = 'JobApplicationListContactSearchList',
	jobApplicationListScanProfile = 'JobApplicationListScanProfile',
	jobApplicationNewApplication = 'JobApplicationNewApplication',
	jobApplicationNewContactApplication = 'JobApplicationNewContactApplication',
	jobServiceHourQrCodeCheckIn = 'JobServiceHourQrCodeCheckIn',
	jobServiceHourQrCodeCheckout = 'JobServiceHourQrCodeCheckout',
}

export function jobRoutes(): Array<AppRouteConfig> {
	return [
		{
			path: 'draft',
			name: JobRouteName.draftJobList,
			component: () => import('../../views/job/DraftList.vue'),
			props: { jobListType: 'draft' },
			meta: {
				breadcrumbs: [breadcrumbs.get('home'), breadcrumbs.get('jobDraft')],
				caslSubject: 'jobs.current_draft_all_jobs',
				caslAction: 'view',
				maskFeature: ['jobs'],
			},
		},
		{
			path: 'current',
			component: { render: c => c('router-view') },
			meta: {
				caslSubject: 'jobs.current_draft_all_jobs',
				caslAction: 'view',
			},
			children: [
				{
					path: '',
					name: JobRouteName.currentJobList,
					component: () => import('../../views/job/CurrentList.vue'),
					props: { jobListType: 'current' },
					meta: {
						breadcrumbs: [
							breadcrumbs.get('home'),
							breadcrumbs.get('jobCurrent'),
						],
						maskFeature: ['jobs'],
					},
				},
				...jobManageApplicationsRoutes(
					JobRouteName.currentJobList,
					breadcrumbs.get('jobCurrent'),
				),
			],
		},
		{
			path: 'all',
			component: { render: c => c('router-view') },
			children: [
				{
					path: '',
					name: JobRouteName.allJobList,
					component: () => import('../../views/job/AllList.vue'),
					props: { jobListType: 'all' },
					meta: {
						breadcrumbs: [breadcrumbs.get('home'), breadcrumbs.get('jobAll')],
						caslSubject: 'jobs.current_draft_all_jobs',
						caslAction: 'view',
						maskFeature: ['jobs'],
					},
				},
				// all job should have its own routes instead of using current
				...jobManageApplicationsRoutes(
					JobRouteName.allJobList,
					breadcrumbs.get('jobAll'),
				),
			],
		},
		{
			path: 'service-hours',
			component: { render: c => c('router-view') },
			children: [
				{
					path: '',
					name: JobRouteName.serviceHourList,
					component: () => import('../../views/job/service-hour/JobList.vue'),
					meta: {
						breadcrumbs: [
							breadcrumbs.get('home'),
							breadcrumbs.get('serviceHours'),
						],
						caslSubject: 'jobs.service_hours',
						caslAction: 'view',
						maskFeature: ['jobs'],
					},
				},
				{
					path: ':jobId',
					component: { render: c => c('router-view') },
					children: [
						{
							path: '',
							name: JobRouteName.serviceHourSessionList,
							component: () =>
								import('../../views/job/service-hour/SessionList.vue'),
							meta: {
								breadcrumbs: [
									breadcrumbs.get('home'),
									breadcrumbs.get('serviceHours'),
									breadcrumbs.get('allTimeslots'),
								],
								caslSubject: 'jobs.service_hours',
								caslAction: 'view',
							},
						},
						{
							path: 'import',
							component: () =>
								import(
									'../../views/job/service-hour/import/JobAttendanceImportContainer.vue'
								),
							props: {
								internal: true,
							},
							meta: {
								caslSubject: 'jobs.manage_applications',
								caslAction: 'edit',
							},
							children: [
								{
									path: '',
									name: JobRouteName.internalJobAttendanceImportList,
									component: () =>
										import(
											'../../views/job/service-hour/import/InternalJobAttendanceImportList.vue'
										),
									meta: {
										breadcrumbs: [
											breadcrumbs.get('home'),
											breadcrumbs.get('serviceHours'),
											breadcrumbs.get('allTimeslots'),
											breadcrumbs.get('jobAttendanceImport'),
										],
									},
								},
								{
									path: 'contact/:id',
									name: JobRouteName.internalJobContactDetails,
									component: () =>
										import('../../views/contact/ContactDetails.vue'),
									meta: {
										breadcrumbs: [
											breadcrumbs.get('home'),
											breadcrumbs.get('serviceHours'),
											breadcrumbs.get('allTimeslots'),
											breadcrumbs.get('jobAttendanceImport'),
											breadcrumbs.get('contactDetail'),
										],
									},
								},
								{
									path: 'search-contact',
									name: JobRouteName.internalJobContactSearchList,
									component: () =>
										import(
											'../../views/job/service-hour/import/JobAttendanceImportContactSearchList.vue'
										),
									meta: {
										breadcrumbs: [
											breadcrumbs.get('home'),
											breadcrumbs.get('serviceHours'),
											breadcrumbs.get('allTimeslots'),
											breadcrumbs.get('jobAttendanceImport'),
											breadcrumbs.get('jobAttendanceImportContactSearch'),
										],
									},
								},
								{
									path: ':index/select',
									name: JobRouteName.internalJobContactSelectList,
									component: () =>
										import(
											'../../views/job/service-hour/import/JobAttendanceImportContactSelectList.vue'
										),
									meta: {
										breadcrumbs: [
											breadcrumbs.get('home'),
											breadcrumbs.get('serviceHours'),
											breadcrumbs.get('allTimeslots'),
											breadcrumbs.get('jobAttendanceImport'),
											breadcrumbs.get('jobAttendanceImportContactSelect'),
										],
									},
								},
							],
						},
						{
							path: ':timeslotId/:timeslotInstanceId',
							component: { render: c => c('router-view') },
							children: [
								{
									path: '',
									name: JobRouteName.jobAttendanceList,
									component: () =>
										import(
											'../../views/job/service-hour/JobAttendanceList.vue'
										),
									meta: {
										breadcrumbs: [
											breadcrumbs.get('home'),
											breadcrumbs.get('serviceHours'),
											breadcrumbs.get('allTimeslots'),
											breadcrumbs.get('jobAttendanceAttendanceList'),
										],
										caslSubject: 'jobs.service_hours',
										caslAction: 'view',
									},
								},
								{
									path: 'scan-profile',
									component: { render: c => c('router-view') },
									children: [
										{
											path: '',
											name: JobRouteName.jobAttendanceListScanProfile,
											component: () =>
												import('../../views/job/service-hour/ScanProfile.vue'),
											meta: {
												breadcrumbs: [
													breadcrumbs.get('home'),
													breadcrumbs.get('serviceHours'),
													breadcrumbs.get('allTimeslots'),
													breadcrumbs.get('jobAttendanceAttendanceList'),
													breadcrumbs.get('serviceHoursScanProfile'),
												],
												caslSubject: 'jobs.service_hours',
												caslAction: 'view',
											},
										},
										{
											//duplicated set to mimic page transition
											path: 'new',
											name: JobRouteName.jobAttendanceListScanProfile,
											component: () =>
												import('../../views/job/service-hour/ScanProfile.vue'),
											meta: {
												breadcrumbs: [
													breadcrumbs.get('home'),
													breadcrumbs.get('serviceHours'),
													breadcrumbs.get('allTimeslots'),
													breadcrumbs.get('jobAttendanceAttendanceList'),
													breadcrumbs.get('addJobApplication'),
												],
												caslSubject: 'jobs.service_hours',
												caslAction: 'view',
											},
										},
									],
								},
								{
									path: 'applications/new',
									name: JobRouteName.jobAttendanceNewApplication,
									component: () =>
										import(
											'../../views/job/service-hour/NewJobApplication.vue'
										),
									meta: {
										breadcrumbs: [
											breadcrumbs.get('home'),
											breadcrumbs.get('serviceHours'),
											breadcrumbs.get('allTimeslots'),
											breadcrumbs.get('jobAttendanceAttendanceList'),
											breadcrumbs.get('addJobApplication'),
										],
										caslSubject: 'jobs.manage_applications',
										caslAction: 'edit',
									},
								},
								{
									path: 'search-contact',
									name:
										JobRouteName.jobAttendanceNewApplicationContactSearchList,
									component: () =>
										import(
											'../../views/job/service-hour/NewJobApplicationContactSearch.vue'
										),
									meta: {
										breadcrumbs: [
											breadcrumbs.get('home'),
											breadcrumbs.get('serviceHours'),
											breadcrumbs.get('allTimeslots'),
											breadcrumbs.get('jobAttendanceAttendanceList'),
											breadcrumbs.get('addJobApplicationContactSearch'),
										],
										caslSubject: 'jobs.manage_applications',
										caslAction: 'edit',
									},
								},
								{
									path: 'check-in',
									name: JobRouteName.jobServiceHourQrCodeCheckIn,
									component: () =>
										import('../../views/job/service-hour/QrCodeScanner.vue'),
									props: { action: 'checkIn' },
									meta: {
										breadcrumbs: [
											breadcrumbs.get('home'),
											breadcrumbs.get('serviceHours'),
											breadcrumbs.get('allTimeslots'),
											breadcrumbs.get('jobAttendanceAttendanceList'),
											breadcrumbs.get('serviceHoursQrCodeScannerCheckin'),
										],
										caslSubject: 'jobs.service_hours',
										caslAction: 'view',
									},
								},
								{
									path: 'check-out',
									name: JobRouteName.jobServiceHourQrCodeCheckout,
									component: () =>
										import('../../views/job/service-hour/QrCodeScanner.vue'),
									props: { action: 'checkOut' },
									meta: {
										breadcrumbs: [
											breadcrumbs.get('home'),
											breadcrumbs.get('serviceHours'),
											breadcrumbs.get('allTimeslots'),
											breadcrumbs.get('jobAttendanceAttendanceList'),
											breadcrumbs.get('serviceHoursQrCodeScannerCheckout'),
										],
										caslSubject: 'jobs.service_hours',
										caslAction: 'view',
									},
								},
								{
									path: 'import',
									component: () =>
										import(
											'../../views/job/service-hour/import/JobAttendanceImportContainer.vue'
										),
									meta: {
										caslSubject: 'jobs.manage_applications',
										caslAction: 'edit',
									},
									children: [
										{
											path: '',
											name: JobRouteName.jobAttendanceImportList,
											component: () =>
												import(
													'../../views/job/service-hour/import/GeneralJobAttendanceImportList.vue'
												),
											meta: {
												breadcrumbs: [
													breadcrumbs.get('home'),
													breadcrumbs.get('serviceHours'),
													breadcrumbs.get('allTimeslots'),
													breadcrumbs.get('jobAttendanceAttendanceList'),
													breadcrumbs.get('jobAttendanceImport'),
												],
											},
										},
										{
											path: 'contact/:id',
											name: JobRouteName.jobContactDetails,
											component: () =>
												import('../../views/contact/ContactDetails.vue'),
											meta: {
												breadcrumbs: [
													breadcrumbs.get('home'),
													breadcrumbs.get('serviceHours'),
													breadcrumbs.get('allTimeslots'),
													breadcrumbs.get('jobAttendanceAttendanceList'),
													breadcrumbs.get('jobAttendanceImport'),
													breadcrumbs.get('contactDetail'),
												],
											},
										},
										{
											path: 'search-contact',
											name: JobRouteName.jobContactSearchList,
											component: () =>
												import(
													'../../views/job/service-hour/import/JobAttendanceImportContactSearchList.vue'
												),
											meta: {
												breadcrumbs: [
													breadcrumbs.get('home'),
													breadcrumbs.get('serviceHours'),
													breadcrumbs.get('allTimeslots'),
													breadcrumbs.get('jobAttendanceAttendanceList'),
													breadcrumbs.get('jobAttendanceImport'),
													breadcrumbs.get('jobAttendanceImportContactSearch'),
												],
											},
										},
										{
											path: ':index/select',
											name: JobRouteName.jobContactSelectList,
											component: () =>
												import(
													'../../views/job/service-hour/import/JobAttendanceImportContactSelectList.vue'
												),
											meta: {
												breadcrumbs: [
													breadcrumbs.get('home'),
													breadcrumbs.get('serviceHours'),
													breadcrumbs.get('allTimeslots'),
													breadcrumbs.get('jobAttendanceAttendanceList'),
													breadcrumbs.get('jobAttendanceImport'),
													breadcrumbs.get('jobAttendanceImportContactSelect'),
												],
											},
										},
									],
								},
							],
						},
					],
				},
			],
		},
	];
}

export function jobEditRoutes(): Array<AppRouteConfig> {
	return [
		{
			path: 'new',
			name: JobRouteName.newJob,
			component: () => import('../../views/job/JobEdit.vue'),
			meta: {
				breadcrumbs: [
					breadcrumbs.get('home'),
					breadcrumbs.get('jobs'),
					breadcrumbs.get('new'),
				],
			},
		},
		{
			path: ':id',
			name: JobRouteName.editJob,
			component: () => import('../../views/job/JobEdit.vue'),
			meta: {
				breadcrumbs: [breadcrumbs.get('home'), breadcrumbs.get('jobs')],
			},
		},
	];
}
